import React, { useState } from 'react';
import { Box, Flex, IconButton, Switch, Text, Tooltip } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { CaretDown, CaretRight, LinkSimple, PencilSimple, Trash, Warning } from 'phosphor-react';

import { IContaCilRegra } from '../../../../../../hooks/useContaCilRegras';
import { C, GrupoCategoria } from '../regrasHelpers';

interface RegrasListProps {
  grupos: GrupoCategoria[];
  onEdit: (r: IContaCilRegra) => void;
  onDuplicate: (r: IContaCilRegra) => void;
  onAskDelete: (r: IContaCilRegra) => void;
  onToggle: (r: IContaCilRegra) => void;
  togglingSig: string | null;
}

function DCBadge({ dc }: { dc?: string | null }) {
  if (!dc) {
    return (
      <Text fontSize="9px" color={C.subtle} fontWeight="700">
        D/C
      </Text>
    );
  }
  const cred = dc === 'C';
  return (
    <Box
      as="span"
      w="18px"
      h="18px"
      borderRadius="md"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      fontSize="10px"
      fontWeight="700"
      bg={cred ? C.vermelhoTint : C.verdeTint}
      color={cred ? C.vermelhoText : C.verdeText}
    >
      {dc}
    </Box>
  );
}

function RegraRow({
  r,
  onEdit,
  onDuplicate,
  onAskDelete,
  onToggle,
  toggling,
}: {
  r: IContaCilRegra;
  onEdit: (r: IContaCilRegra) => void;
  onDuplicate: (r: IContaCilRegra) => void;
  onAskDelete: (r: IContaCilRegra) => void;
  onToggle: (r: IContaCilRegra) => void;
  toggling: boolean;
}) {
  const isHardcoded = r.origem === 'hardcoded';
  const temPoc = !!(r.poc_codigo || (r.poc_search && r.poc_search.length));
  return (
    <Flex
      align="center"
      gap={3}
      px={4}
      py={3}
      minH="56px"
      borderBottomWidth="1px"
      borderColor="rgba(15,23,42,0.05)"
      opacity={r.ativa ? 1 : 0.55}
      _hover={{ bg: C.rowHover }}
      transition="background 0.18s ease, opacity 0.22s ease"
      role="group"
    >
      <Switch
        size="sm"
        colorScheme="green"
        isChecked={r.ativa}
        isDisabled={toggling}
        onChange={() => onToggle(r)}
      />
      <Box flex={1} minW={0}>
        <Flex align="center" gap={2} wrap="wrap">
          <Text fontSize="sm" fontWeight="600" color={C.texto} isTruncated maxW="320px">
            {r.kw.join(', ')}
          </Text>
          {r.banco && (
            <Text fontSize="9px" color={C.subtle} fontWeight="700" textTransform="uppercase">
              {r.banco}
            </Text>
          )}
        </Flex>
        {!temPoc && (
          <Flex align="center" gap={1} mt={1} color={C.ambarText}>
            <Warning size={11} weight="fill" color={C.ambar} />
            <Text fontSize="10px" fontWeight="700">
              sem POC — não fecha sozinho
            </Text>
          </Flex>
        )}
      </Box>

      <DCBadge dc={r.dc} />

      <Box
        as="span"
        px={2}
        py={0.5}
        borderRadius="full"
        fontSize="9px"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="0.06em"
        bg={isHardcoded ? 'rgba(148,163,184,0.12)' : 'rgba(236,72,153,0.10)'}
        color={isHardcoded ? C.muted : C.rosaHover}
        flexShrink={0}
      >
        {isHardcoded ? 'padrão' : 'personalizada'}
      </Box>

      {r.criada_por_ia && (
        <Box
          as="span"
          px={2}
          py={0.5}
          borderRadius="full"
          fontSize="9px"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.06em"
          color="white"
          bgGradient="linear(135deg, #6366F1, #EC4899)"
          flexShrink={0}
          title="Criada pela IA (ainda não revisada). Editar remove esta marca."
        >
          IA
        </Box>
      )}

      <Box minW="150px" maxW="200px">
        {temPoc ? (
          <Flex
            align="center"
            gap={1}
            bg="rgba(99,102,241,0.10)"
            color={C.indigo}
            borderRadius="lg"
            px={2}
            py={1}
          >
            <LinkSimple size={12} weight="bold" />
            <Text fontSize="11px" fontWeight="600" isTruncated>
              {r.poc_codigo
                ? `${r.poc_codigo}${r.poc_nome ? ` · ${r.poc_nome}` : ''}`
                : (r.poc_search || []).join(', ')}
            </Text>
          </Flex>
        ) : (
          <Text fontSize="11px" color={C.subtle}>
            —
          </Text>
        )}
      </Box>

      <Flex gap={1}>
        {isHardcoded ? (
          <Tooltip label="Personalizar (cria uma regra personalizada editável)" hasArrow>
            <IconButton
              aria-label="Personalizar"
              icon={<PencilSimple size={16} />}
              size="sm"
              variant="ghost"
              borderRadius="full"
              color={C.subtle}
              _hover={{ bg: 'rgba(236,72,153,0.10)', color: C.rosaHover }}
              onClick={() => onDuplicate(r)}
            />
          </Tooltip>
        ) : (
          <>
            <Tooltip label="Editar" hasArrow>
              <IconButton
                aria-label="Editar"
                icon={<PencilSimple size={16} />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                color={C.subtle}
                _hover={{ bg: 'rgba(15,23,42,0.06)', color: C.texto }}
                onClick={() => onEdit(r)}
              />
            </Tooltip>
            <Tooltip label="Excluir" hasArrow>
              <IconButton
                aria-label="Excluir"
                icon={<Trash size={16} />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                color={C.subtle}
                _hover={{ bg: 'rgba(239,68,68,0.10)', color: C.vermelho }}
                onClick={() => onAskDelete(r)}
              />
            </Tooltip>
          </>
        )}
      </Flex>
    </Flex>
  );
}

export function RegrasList({
  grupos,
  onEdit,
  onDuplicate,
  onAskDelete,
  onToggle,
  togglingSig,
}: RegrasListProps) {
  const [aberto, setAberto] = useState<Record<string, boolean>>({});

  return (
    <Flex direction="column" gap={3}>
      {grupos.map(g => {
        // Default: grupo com regra personalizada já abre; só de padrão recolhe.
        const temPersonalizada = g.regras.some(r => r.origem === 'personalizada');
        const isOpen = aberto[g.categoria] ?? temPersonalizada;
        return (
          <Box
            key={g.categoria}
            bg="rgba(255,255,255,0.65)"
            backdropFilter="blur(28px) saturate(200%)"
            sx={{ WebkitBackdropFilter: 'blur(28px) saturate(200%)' }}
            borderWidth="1px"
            borderColor="rgba(255,255,255,0.6)"
            borderRadius="3xl"
            overflow="hidden"
          >
            <Flex
              align="center"
              justify="space-between"
              px={4}
              py={3}
              cursor="pointer"
              _hover={{ bg: C.rowHover }}
              onClick={() => setAberto(p => ({ ...p, [g.categoria]: !isOpen }))}
            >
              <Flex align="center" gap={2}>
                <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
                  {isOpen ? (
                    <CaretDown size={14} weight="bold" color={C.subtle} />
                  ) : (
                    <CaretRight size={14} weight="bold" color={C.subtle} />
                  )}
                </motion.div>
                <Text fontSize="sm" fontWeight="600" color={C.texto}>
                  {g.categoria}
                </Text>
                <Text fontSize="xs" color={C.muted}>
                  · {g.regras.length} regra{g.regras.length !== 1 ? 's' : ''}
                </Text>
              </Flex>
              <Flex align="center" gap={2}>
                {g.comPoc > 0 && (
                  <Box
                    bg="rgba(99,102,241,0.10)"
                    color={C.indigo}
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {g.comPoc} com POC
                  </Box>
                )}
                {g.comPoc < g.regras.length && (
                  <Box
                    bg={C.ambarTint}
                    color={C.ambarText}
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {g.regras.length - g.comPoc} sem POC
                  </Box>
                )}
              </Flex>
            </Flex>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <Box borderTopWidth="1px" borderColor={C.hairline}>
                    {g.regras.map(r => (
                      <RegraRow
                        key={r.id}
                        r={r}
                        onEdit={onEdit}
                        onDuplicate={onDuplicate}
                        onAskDelete={onAskDelete}
                        onToggle={onToggle}
                        toggling={togglingSig === r.kw_signature}
                      />
                    ))}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        );
      })}
    </Flex>
  );
}
